import type { App } from 'vue';
import type {
  LocationQueryRaw,
  NavigationFailure,
  NavigationGuardNext,
  RouteLocationNormalized,
  RouteParamsRaw,
  Router,
  RouteRecordRaw,
} from 'vue-router';
import {
  createRouter,
  createWebHashHistory,
  createWebHistory,
  createMemoryHistory,
} from 'vue-router';
import { BaseService } from '../base';

// 路由历史记录项
export interface RouterHistoryItem {
  // 路由路径
  path: string;
  // 路由名称
  name?: string | symbol;
  // 路由参数
  params?: RouteParamsRaw;
  // 查询参数
  query?: LocationQueryRaw;
  // 时间戳
  timestamp: number;
  // 来源路由
  from?: string;
}

// 路由服务配置
export interface RouterServiceConfig {
  // 路由模式：'history' | 'hash'
  mode?: 'history' | 'hash';
  // 基础路径
  base?: string;
  // 路由配置
  routes?: RouteRecordRaw[];
  // 滚动行为配置
  scrollBehavior?: (
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    savedPosition: { left: number; top: number } | null,
  ) => { left: number; top: number } | void;
  // 路由守卫配置
  guards?: {
    beforeEach?: (
      to: RouteLocationNormalized,
      from: RouteLocationNormalized,
      next: NavigationGuardNext,
    ) => void;
    afterEach?: (to: RouteLocationNormalized, from: RouteLocationNormalized) => void;
  };
  // 历史记录最大条数
  maxHistorySize?: number;
}

// 路由服务实现
export class RouterService extends BaseService {
  private router: Router | null = null;
  private config: RouterServiceConfig;
  private history: RouterHistoryItem[] = [];
  private readonly maxHistorySize: number;

  constructor(config: RouterServiceConfig = {}) {
    super('RouterService');
    this.config = {
      mode: 'history',
      base: '/',
      routes: [],
      maxHistorySize: 50,
      ...config,
    };
    this.maxHistorySize = this.config.maxHistorySize || 50;
  }

  // 初始化服务
  protected async onInitialize(): Promise<void> {
    this.router = this.createRouter();
    this.setupGuards();
  }

  // 销毁服务
  protected async onDestroy(): Promise<void> {
    // 注意：Vue Router 的守卫（beforeEach/afterEach）一旦注册就无法移除
    // 这是 Vue Router 的限制，但通常不会造成内存泄露，因为 router 实例会被销毁
    this.router = null;
    this.history = [];
    // @ts-expect-error - 清理 config 引用
    this.config = null;
  }

  // 创建路由实例
  createRouter(): Router {
    if (this.router) {
      return this.router;
    }

    const { mode, base, routes, scrollBehavior } = this.config;

    // SSR 环境下使用 createMemoryHistory，避免跨请求共享路由状态
    const router = createRouter({
      history: import.meta.server
        ? createMemoryHistory(base)
        : mode === 'hash'
          ? createWebHashHistory(base)
          : createWebHistory(base),
      routes: routes || [],
      scrollBehavior:
        scrollBehavior ||
        ((_to, _from, savedPosition) => {
          if (savedPosition) {
            return savedPosition;
          } else {
            return { top: 0 };
          }
        }),
    });

    this.router = router;
    return router;
  }

  // 设置路由守卫
  private setupGuards(): void {
    if (!this.router) {
      return;
    }

    const { beforeEach, afterEach } = this.config.guards || {};

    if (beforeEach) {
      this.router.beforeEach(beforeEach);
    }

    // 添加历史记录守卫
    this.router.afterEach((to, from) => {
      this.addHistoryRecord(to, from);

      // 调用用户自定义的 afterEach
      if (afterEach) {
        afterEach(to, from);
      }
    });
  }

  // 添加历史记录
  private addHistoryRecord(to: RouteLocationNormalized, from: RouteLocationNormalized): void {
    const historyItem: RouterHistoryItem = {
      path: to.path,
      name: to.name ?? undefined,
      params: to.params,
      query: to.query,
      timestamp: Date.now(),
      from: from.path,
    };

    this.history.push(historyItem);

    // 限制历史记录数量
    if (this.history.length > this.maxHistorySize) {
      this.history.shift();
    }
  }

  // 安装到 Vue 应用
  install(app: App): void {
    const router = this.createRouter();
    app.use(router);
  }

  // 获取路由实例
  getRouter(): Router | null {
    return this.router;
  }

  // 添加路由
  addRoute(parentName: string | undefined, route: RouteRecordRaw): void {
    if (this.router) {
      this.router.addRoute(parentName as string, route);
    }
  }

  // 移除路由
  removeRoute(name: string): void {
    if (this.router) {
      this.router.removeRoute(name);
    }
  }

  // 获取所有路由
  getRoutes() {
    return this.router?.getRoutes() || [];
  }

  // 路由跳转
  push(to: RouteRecordRaw | string): Promise<NavigationFailure | void | undefined> {
    return this.router?.push(to) || Promise.reject(new Error('Router not initialized'));
  }

  // 路由替换
  replace(to: RouteRecordRaw | string): Promise<NavigationFailure | void | undefined> {
    return this.router?.replace(to) || Promise.reject(new Error('Router not initialized'));
  }

  // 路由回退
  back(): void {
    this.router?.back();
  }

  // 路由前进
  forward(): void {
    this.router?.forward();
  }

  // 路由跳转到指定位置
  go(delta: number): void {
    this.router?.go(delta);
  }

  // 获取当前路由
  getCurrentRoute() {
    return this.router?.currentRoute.value;
  }

  // 检查路由是否存在
  hasRoute(name: string): boolean {
    return this.router?.hasRoute(name) || false;
  }

  // 解析路由
  resolve(to: RouteRecordRaw | string) {
    return this.router?.resolve(to);
  }

  // 获取路由历史记录
  getHistory(): RouterHistoryItem[] {
    return [...this.history];
  }

  // 获取最近N条历史记录
  getRecentHistory(count: number = 10): RouterHistoryItem[] {
    return this.history.slice(-count);
  }

  // 清空历史记录
  clearHistory(): void {
    this.history = [];
  }

  // 获取历史记录数量
  getHistorySize(): number {
    return this.history.length;
  }
}

// 创建路由服务实例
export const createRouterService = (config: RouterServiceConfig = {}): RouterService => {
  return new RouterService(config);
};

// 全局路由服务实例
let globalRouterService: RouterService | null = null;

// 初始化路由服务
export const initRouterService = async (
  config: RouterServiceConfig = {},
): Promise<RouterService> => {
  // SSR 环境下为每个请求创建新实例，避免跨请求共享路由状态
  if (import.meta.server) {
    const newService = createRouterService(config);
    await newService.initialize();
    return newService;
  }

  if (!globalRouterService) {
    globalRouterService = createRouterService(config);
    await globalRouterService.initialize();
  }
  return globalRouterService;
};

// 获取路由服务实例
export const getRouterService = (): RouterService | null => {
  return globalRouterService;
};

// 销毁路由服务
export const destroyRouterService = async (): Promise<void> => {
  if (globalRouterService) {
    await globalRouterService.destroy();
    globalRouterService = null;
  }
};
